import React from "react";
import styled, { keyframes, css } from "styled-components";
import {
  FiShield,
  FiShieldOff,
  FiAlertTriangle,
  FiLoader,
} from "react-icons/fi";

// Helper function to determine verification status
export const getVerificationStatus = (user) => {
  if (!user) return "unverified";

  if (user?.emailVerified) {
    return "verified";
  } else if (user.email && !user?.emailVerified) {
    return "pending";
  } else {
    return "unverified";
  }
};

const VerificationStatusBadge = ({
  loading = false,
  status = "unverified",
  onClick = null,
  showTooltip = true,
  size = "sm",
  big,
}) => {
  const getIcon = () => {
    if (loading) {
      return <FiLoader size={size === "sm" ? 12 : 14} />;
    }

    switch (status) {
      case "verified":
        return <FiShield size={size === "sm" ? 12 : 14} />;
      case "unverified":
        return <FiShieldOff size={size === "sm" ? 12 : 14} />;
      case "pending":
        return <FiAlertTriangle size={size === "sm" ? 12 : 14} />;
      default:
        return <FiShieldOff size={size === "sm" ? 12 : 14} />;
    }
  };

  const getText = () => {
    if (loading) {
      return "Učitava...";
    }

    switch (status) {
      case "verified":
        return "Verifikovano";
      case "unverified":
        return "Nije verifikovano";
      case "pending":
        return "Na čekanju";
      default:
        return "Nepoznato";
    }
  };

  const getTooltipText = () => {
    if (loading) {
      return "Proverava se status verifikacije...";
    }

    switch (status) {
      case "verified":
        return "Email adresa je uspešno verifikovana";
      case "unverified":
        return "Kliknite da verifikujete email adresu";
      case "pending":
        return "Kliknite da pošaljete verifikaciju ponovo";
      default:
        return "Status verifikacije nije poznat";
    }
  };

  return (
    <BadgeContainer
      $clickable={!!onClick && !loading}
      onClick={!loading ? onClick : undefined}
      style={{ position: showTooltip ? "relative" : "static" }}
    >
      {big ? (
        <Badge
          $status={loading ? "loading" : status}
          $clickable={!!onClick && !loading}
          $loading={loading}
          style={{ fontSize: ".9rem" }}
        >
          {getIcon()}
          {getText()}
        </Badge>
      ) : (
        <Badge
          $status={loading ? "loading" : status}
          $clickable={!!onClick && !loading}
          $loading={loading}
          style={{ padding: ".4rem", borderRadius: "50%" }}
        >
          {getIcon()}
        </Badge>
      )}
      {showTooltip && <Tooltip>{getTooltipText()}</Tooltip>}
    </BadgeContainer>
  );
};

export default VerificationStatusBadge;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.015);
    opacity: 0.8;
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const BadgeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: all 0.3s ease;

  &:hover {
    ${({ $clickable }) => $clickable && "transform: scale(1.025);"}
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  ${({ $loading }) =>
    $loading &&
    css`
      svg {
        animation: ${spin} 1s linear infinite;
      }
    `}

  ${({ $status }) => {
    switch ($status) {
      case "loading":
        return css`
          background: linear-gradient(
            90deg,
            rgba(156, 163, 175, 0.2) 0%,
            rgba(156, 163, 175, 0.4) 50%,
            rgba(156, 163, 175, 0.2) 100%
          );
          background-size: 200px 100%;
          animation: ${shimmer} 1.5s ease-in-out infinite;
          color: #9ca3af;
          border: 1px solid rgba(156, 163, 175, 0.4);

          svg {
            color: #9ca3af;
            animation: ${spin} 1s linear infinite;
          }
        `;
      case "verified":
        return `
          background: rgba(76, 175, 80, 0.2);
          color: #4caf50;
          border: 1px solid rgba(76, 175, 80, 0.4);
          
          svg {
            color: #4caf50;
            filter: drop-shadow(0 0 4px rgba(76, 175, 80, 0.5));
          }
        `;
      case "unverified":
        return css`
          background: rgba(255, 152, 0, 0.2);
          color: #ff9800;
          border: 1px solid rgba(255, 152, 0, 0.4);
          animation: ${pulse} 2s ease-in-out infinite;

          svg {
            color: #ff9800;
            filter: drop-shadow(0 0 4px rgba(255, 152, 0, 0.5));
          }
        `;
      case "pending":
        return `
          background: rgba(33, 150, 243, 0.2);
          color: #2196f3;
          border: 1px solid rgba(33, 150, 243, 0.4);
          
          svg {
            color: #2196f3;
            filter: drop-shadow(0 0 4px rgba(33, 150, 243, 0.5));
          }
        `;
      default:
        return `
          background: rgba(96, 125, 139, 0.2);
          color: #607d8b;
          border: 1px solid rgba(96, 125, 139, 0.4);
          
          svg {
            color: #607d8b;
          }
        `;
    }
  }}

  &:hover {
    ${({ $clickable, $status }) =>
      $clickable &&
      !($status === "loading") &&
      (() => {
        switch ($status) {
          case "verified":
            return `
              background: rgba(76, 175, 80, 0.3);
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
            `;
          case "unverified":
            return `
              background: rgba(255, 152, 0, 0.3);
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
            `;
          case "pending":
            return `
              background: rgba(33, 150, 243, 0.3);
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
            `;
          default:
            return "";
        }
      })()}
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 0.5rem;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  white-space: nowrap;
  z-index: 10000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid rgba(0, 0, 0, 0.9);
  }

  ${BadgeContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;
